import { useState } from "react";
import { useVoucherToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useBookingStore } from "@/stores/bookingStore";
import { t as translate } from "@/locales";
import type { BookingLanguage } from "@/types/booking";

export type VoucherItem = { stripe_product_id: string; unit_amount?: number };

interface VoucherResponse {
  coupon?: {
    id: string;
    percent_off?: number;
    amount_off?: number;
    duration?: "once" | "repeating" | "forever" | null;
    duration_in_months?: number | null;
  };
  promotionCode?: {
    code: string;
  };
  eligibility?: {
    isEligible: boolean;
    message?: string;
    reason?: "below_minimum";
  };
  calculatedDiscount?: number;
}

/**
 * Validates the invitation code against a cart (validate-voucher-v3) and
 * stores the discount it quotes for that cart. The quote is an amount for
 * these items, so a cart that changes needs it asked again: the input does
 * it while it is shown, and a flow whose cart changes elsewhere (the
 * membership-first plan step, under the rail) asks again from there.
 */
export function useVoucherValidation(
  language: BookingLanguage,
  items: VoucherItem[],
) {
  const [loading, setLoading] = useState(false);
  const { toast } = useVoucherToast();
  const t = {
    invalid: translate(language, "invitationCode.invalid"),
    success: translate(language, "invitationCode.success"),
    error: translate(language, "invitationCode.error"),
    notEligible: translate(language, "invitationCode.notEligible"),
    partialEligible: translate(language, "invitationCode.partialEligible"),
  };
  const invitationCode = useBookingStore((state) => state.invitationCode);
  const setDiscount = useBookingStore((state) => state.setDiscount);
  const clearInvitationCode = useBookingStore(
    (state) => state.clearInvitationCode,
  );

  const validateVoucher = async (isRevalidation = false) => {
    const code = invitationCode.trim();
    if (!code) return;

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke<VoucherResponse>(
        "validate-voucher-v3",
        {
          body: {
            code: invitationCode.trim(),
            items: items,
            // Picks the same Stripe env (sandbox vs live) the checkout will use.
            origin: window.location.origin,
          },
        },
      );

      if (error || !data?.coupon) {
        // Clear the field on invalid code
        clearInvitationCode();
        toast({
          title: t.invalid,
        });
        return;
      }

      // Check eligibility
      if (data.eligibility && !data.eligibility.isEligible) {
        clearInvitationCode();
        const toastTitle =
          data.eligibility.reason === "below_minimum"
            ? translate(language, "invitationCode.belowMinimum") ||
              data.eligibility.message ||
              t.notEligible
            : t.notEligible;
        toast({ title: toastTitle });
        return;
      }

      // Use the calculated discount from backend
      const discountValue =
        data.calculatedDiscount !== undefined
          ? data.calculatedDiscount
          : data.coupon.amount_off || 0;

      const newDiscount = {
        code: invitationCode.trim(),
        type: "fixed_amount" as const,
        value: discountValue,
        duration: data.coupon?.duration ?? null,
        durationInMonths: data.coupon?.duration_in_months ?? null,
      };

      setDiscount(newDiscount);

      // Show warning if code only applies to some products
      console.log("[InvitationCodeInput] Eligibility check:", {
        hasMessage: !!data.eligibility?.message,
        message: data.eligibility?.message,
        isEligible: data.eligibility?.isEligible,
      });

      if (data.eligibility?.message) {
        console.log("[InvitationCodeInput] Showing partial eligibility toast");
        toast({
          title: t.success,
          description: t.partialEligible,
        });
      } else {
        if (!isRevalidation) {
          console.log("[InvitationCodeInput] Showing success toast");
          toast({
            title: t.success,
          });
        }
      }
    } catch (error) {
      console.error("Error validating voucher:", error);
      if (!isRevalidation) {
        toast({
          title: t.error,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return { validateVoucher, loading };
}
